<template>
  <div>
    <LoadingSpinner v-if="isLoading" />
    <div v-else>
      <AppPageHeader :title="project.name" />

      <div class="card">
        <CreateProjectForm
          :key="project.id"
          :project="project"
          :save-function="saveProject"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import CreateProjectForm from '../components/ProjectForm.vue'
import { Project } from '@/models/project'
import ProjectService from '@/services/ProjectService'
import CustomerService from '@/services/CustomerService'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import ProposalService from '@/services/ProposalService'

useHead({ title: 'Edit project' })

const $route = useRoute()
const $router = useRouter()
const { notify } = useNotification()
const projectService = new ProjectService()
const customerService = new CustomerService()
const queryClient = useQueryClient()
const projectId = $route.params.projectId as string | ''
const customerId = $route.params.customerId as string | ''
const { data: project, isLoading } = useQuery({
  queryKey: ['project', projectId],
  queryFn: () => projectService.find(projectId)
})

const { mutateAsync: saveProject } = useMutation({
  mutationFn: async (project: Project) => {
    const updatedProject = await customerService.updateCustomerProject(customerId, project.id, project)
    if (customerId !== project.customerId) {
      const proposalService = new ProposalService()
      const proposal = await proposalService.find(project.id)
      if (proposal) {
        const updatedProposal = await proposalService.update(proposal.id, { ...proposal, customerId: project.customerId })
        queryClient.setQueryData(['proposal', proposal.id], updatedProposal)
      }
    }
    return updatedProject
  },
  onSuccess: async (project: Project) => {
    queryClient.setQueryData(['project', project.id], project)
    await queryClient.invalidateQueries({ queryKey: ['projects', 'all', project.status] })
    notify({ type: 'success', title: 'Success', text: 'Project updated successfully' })
    $router.push({ name: 'ShowProject', params: { projectId: project.id, customerId: project.customerId } })
  },
  onError: (error) => {
    notify({ type: 'error', title: 'Error', text: error.message || 'An error occurred while trying to update the project' })
  }
})
</script>
